
/**
 * The Riz SDK protocol. One version, one event list, one error taxonomy,
 * shared by the browser SDK, the host wrapper, and the server routes.
 *
 * Error codes are additive only: never renumber or repurpose one, because a
 * published game may be branching on it.
 */

export const SDK_PROTOCOL_VERSION = 1;

/** Reported by the game as it runs. Metered against the gameplay budget. */
export const SDK_GAMEPLAY_EVENTS = [
  'game_loaded',
  'game_started',
  'level_started',
  'level_completed',
  'score_submitted',
  'achievement_unlocked',
  'game_paused',
  'game_resumed',
  'session_heartbeat',
  'tutorial_started',
  'tutorial_completed',
  'checkpoint_reached',
  'item_collected',
  'powerup_used',
  'custom_event',
  'game_completed_percentage',
  'first_frame_rendered',
  'funnel_step',
  'game_ended',
] as const;

/**
 * Advertising events are metered apart from gameplay: a session that shows
 * three rewarded ads must not lose a third of its gameplay telemetry budget.
 */
export const SDK_AD_EVENTS = [
  'ad_requested',
  'ad_filled',
  'ad_no_fill',
  'ad_started',
  'ad_completed',
  'ad_skipped',
  'ad_failed',
  'ad_clicked',
  'ad_reward_granted',
  'banner_requested',
  'banner_filled',
  'banner_cleared',
] as const;

export const SDK_PLATFORM_EVENTS = [
  'data_saved',
  'data_migrated',
  'room_updated',
  'room_left',
  'invite_created',
  'invite_followed',
  'content_shared',
  'purchase_started',
  'purchase_completed',
  'purchase_cancelled',
  'purchase_failed',
  'entitlement_consumed',
] as const;

export const SDK_EVENTS = [
  ...SDK_GAMEPLAY_EVENTS,
  ...SDK_AD_EVENTS,
  ...SDK_PLATFORM_EVENTS,
] as const;

export type SdkEvent = (typeof SDK_EVENTS)[number];

export const SDK_EVENT_SET: ReadonlySet<string> = new Set<string>(SDK_EVENTS);
export const SDK_AD_EVENT_SET: ReadonlySet<string> = new Set<string>(SDK_AD_EVENTS);

/**
 * Events allowed before `game_started`. Everything else needs a started run,
 * so an event stream cannot report progress in a game that never began.
 */
export const SDK_PRELUDE_EVENTS: ReadonlySet<string> = new Set<string>([
  'game_loaded',
  'first_frame_rendered',
  'banner_requested',
  'banner_filled',
  'banner_cleared',
  'data_saved',
  'data_migrated',
  'purchase_started',
  'purchase_completed',
  'purchase_cancelled',
  'purchase_failed',
  'entitlement_consumed',
]);

export const SDK_LIMITS = {
  maxScore: 1_000_000,
  maxEventsPerSession: 500,
  maxEventsPerMinute: 120,
  maxAdEventsPerSession: 120,
  maxRpPerRun: 50,
  scorePerRp: 100,
  sessionTtlMs: 6 * 60 * 60 * 1000,
  heartbeatMs: 30_000,
  idleAfterMs: 60_000,
  maxPayloadBytes: 8_000,
  maxDataBytes: 262_144,
  dataWriteDebounceMs: 1_000,
  bannerRefreshMs: 30_000,
  maxBannerRefreshPerSession: 120,
  adRequestTimeoutMs: 12_000,
  rewardMinWatchMs: 5_000,
  roomTtlMs: 10 * 60 * 1000,
} as const;

export const SDK_ERRORS = {
  invalidEvent: 'invalid_event',
  invalidPayload: 'invalid_payload',
  notAuthenticated: 'not_authenticated',
  unknownGame: 'unknown_game',
  notConnected: 'not_connected',
  notLive: 'not_live',
  originMismatch: 'origin_mismatch',
  originNotAuthorised: 'origin_not_authorised',
  sessionInactive: 'session_inactive',
  sessionExpired: 'session_expired',
  rateLimited: 'rate_limited',
  serverError: 'server_error',
  capabilityDisabled: 'capability_disabled',
  protocolUnsupported: 'protocol_unsupported',
  notInitialized: 'not_initialized',
  timeout: 'timeout',
  unfilled: 'unfilled',
  adCooldown: 'ad_cooldown',
  adNoFill: 'ad_no_fill',
  adDismissed: 'ad_dismissed',
  bannerCooldown: 'banner_cooldown',
  bannerNotVisible: 'banner_not_visible',
  bannerInvalidSize: 'banner_invalid_size',
  bannerMaxRefresh: 'banner_max_refresh',
  dataLimitExceeded: 'data_limit_exceeded',
  purchaseCancelled: 'purchase_cancelled',
  purchaseFailed: 'purchase_failed',
  roomUnavailable: 'room_unavailable',
  scopeUnavailable: 'scope_unavailable',
  /** Present from the first release so a client can render a newer failure. */
  unknownError: 'unknown_error',
} as const;

export type SdkErrorCode = (typeof SDK_ERRORS)[keyof typeof SDK_ERRORS];

export const SDK_ERROR_STATUS: Record<SdkErrorCode, number> = {
  invalid_event: 400,
  invalid_payload: 400,
  not_authenticated: 401,
  unknown_game: 404,
  not_connected: 403,
  not_live: 403,
  origin_mismatch: 403,
  origin_not_authorised: 403,
  session_inactive: 409,
  session_expired: 410,
  rate_limited: 429,
  server_error: 500,
  capability_disabled: 403,
  protocol_unsupported: 400,
  not_initialized: 400,
  timeout: 504,
  unfilled: 200,
  ad_cooldown: 429,
  ad_no_fill: 200,
  ad_dismissed: 200,
  banner_cooldown: 429,
  banner_not_visible: 400,
  banner_invalid_size: 400,
  banner_max_refresh: 429,
  data_limit_exceeded: 413,
  purchase_cancelled: 200,
  purchase_failed: 402,
  room_unavailable: 409,
  scope_unavailable: 200,
  unknown_error: 500,
};

/**
 * Retrying cannot change the outcome. A queue that retries these burns its
 * attempt budget for nothing.
 */
export const SDK_PERMANENT_ERRORS: ReadonlySet<string> = new Set<string>([
  SDK_ERRORS.invalidEvent,
  SDK_ERRORS.invalidPayload,
  SDK_ERRORS.unknownGame,
  SDK_ERRORS.notConnected,
  SDK_ERRORS.notLive,
  SDK_ERRORS.originMismatch,
  SDK_ERRORS.originNotAuthorised,
  SDK_ERRORS.capabilityDisabled,
  SDK_ERRORS.protocolUnsupported,
  SDK_ERRORS.notInitialized,
  SDK_ERRORS.bannerInvalidSize,
  SDK_ERRORS.dataLimitExceeded,
  SDK_ERRORS.purchaseCancelled,
]);

/** Ends the session outright; everything queued behind drains to failure. */
export const SDK_FATAL_ERRORS: ReadonlySet<string> = new Set<string>([
  SDK_ERRORS.sessionInactive,
  SDK_ERRORS.sessionExpired,
  SDK_ERRORS.notLive,
  SDK_ERRORS.originMismatch,
  SDK_ERRORS.originNotAuthorised,
]);

/**
 * What a game may do, granted per game by the operator control plane. A module
 * whose capability is absent stays on the SDK object and rejects with
 * `capability_disabled`, so a revoked feature is a handled error rather than a
 * missing method.
 */
export const SDK_CAPABILITIES = [
  'game.lifecycle',
  'score.submit',
  'leaderboard.read',
  'leaderboard.scopes',
  'leaderboard.seasons',
  'ads.rewarded',
  'ads.interstitial',
  'ads.banner',
  'data.storage',
  'user.profile',
  'user.authPrompt',
  'social.rooms',
  'social.invites',
  'social.share',
  'purchases.buy',
  'analytics.custom',
  'rewards.rp',
] as const;

export type SdkCapability = (typeof SDK_CAPABILITIES)[number];

/**
 * Defaults by integration status, so a newly connected game is safe before an
 * operator has touched it: listed games get nothing, testing games get the
 * non-revenue surface, verified games get the standard set. Anything beyond
 * this is an explicit per-game grant.
 */
export const SDK_DEFAULT_CAPABILITIES: Record<string, readonly SdkCapability[]> = {
  LISTED: [],
  TESTING: [
    'game.lifecycle',
    'score.submit',
    'leaderboard.read',
    'user.profile',
    'analytics.custom',
    'data.storage',
  ],
  VERIFIED: [
    'game.lifecycle',
    'score.submit',
    'leaderboard.read',
    'user.profile',
    'analytics.custom',
    'data.storage',
    'rewards.rp',
    'ads.rewarded',
    'ads.interstitial',
  ],
};

export function capabilitiesFor(
  integrationStatus: string,
  grants: readonly string[] = [],
  revocations: readonly string[] = [],
): SdkCapability[] {
  const base = SDK_DEFAULT_CAPABILITIES[integrationStatus] ?? [];
  const resolved = new Set<string>(base);
  for (const grant of grants) if ((SDK_CAPABILITIES as readonly string[]).includes(grant)) resolved.add(grant);
  for (const revocation of revocations) resolved.delete(revocation);
  return [...resolved] as SdkCapability[];
}

export const BANNER_SIZES = {
  leaderboard: { width: 728, height: 90 },
  medium: { width: 300, height: 250 },
  mobile: { width: 320, height: 50 },
  main: { width: 468, height: 60 },
  largeMobile: { width: 320, height: 100 },
} as const;

export type BannerSize = keyof typeof BANNER_SIZES;

export function sdkErrorBody(code: SdkErrorCode, message: string) {
  return { error: message, code };
}

export function clampScore(value: unknown): number | null {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(SDK_LIMITS.maxScore, Math.floor(numeric)));
}

export function rpForScore(score: number): number {
  return Math.max(0, Math.min(SDK_LIMITS.maxRpPerRun, Math.floor(score / SDK_LIMITS.scorePerRp)));
}

export function rewardsEnabled(game: { status: string; integrationStatus: string }): boolean {
  return game.status === 'PUBLISHED' && game.integrationStatus === 'VERIFIED';
}
